"use client";

import styled from "styled-components";
import { space } from "styled-system";

import type { CarryTheme } from "../../theme";
import { buttonIconSizesTheme, buttonSizesTheme } from "./theme";
import type {
  ButtonProps,
  ButtonSizeVariant,
  ButtonThemeVariant,
} from "./types";
import { buttonSizes, buttonVariants } from "./types";

type ThemedProps = {
  theme: CarryTheme;
} & ButtonProps;

const removePointerEvents = ({ disabled, $isLoading, as }: ThemedProps) => {
  if ((disabled || $isLoading) && as && as !== "button") {
    return `
      pointer-events: none;
    `;
  }
  return "";
};

const getButtonVariantProp =
  (error: boolean | undefined, prop: keyof ButtonThemeVariant) =>
  ({ theme, variant = buttonVariants.PRIMARY }: ThemedProps) => {
    if (typeof theme.button[variant][prop] === "function") {
      return theme.button[variant][prop](error);
    }
    return null;
  };

const getButtonSizeVariantProp =
  (prop: keyof ButtonSizeVariant) =>
  ({ size = buttonSizes.M }: ThemedProps) => {
    return buttonSizesTheme[size][prop];
  };

const getButtonIconSize =
  () =>
  ({ size = buttonSizes.M }: ThemedProps) => {
    return buttonIconSizesTheme[size];
  };

const StyledButton = styled.button.attrs<ButtonProps>(
  ({ initChatSession, disabled, $isLoading }) => ({
    "data-init-chat-session": initChatSession || undefined,
    disabled: disabled || $isLoading,
  }),
)<ButtonProps>`
  padding: ${({ $onlyIcon }) => ($onlyIcon ? 0 : "0 10px")};
  min-width: ${({ $onlyIcon }) =>
    $onlyIcon ? 0 : getButtonSizeVariantProp("width")};
  width: ${({ $onlyIcon }) => ($onlyIcon ? getButtonIconSize : "max-content")};
  height: ${({ $onlyIcon }) =>
    $onlyIcon ? getButtonIconSize : getButtonSizeVariantProp("height")};
  font-size: ${getButtonSizeVariantProp("fontSize")};
  font-weight: 600;
  line-height: 156%;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: ${({ $error }) =>
    getButtonVariantProp($error, "background")};
  border: ${({ $error }) => getButtonVariantProp($error, "border")};
  color: ${({ $error }) => getButtonVariantProp($error, "color")};
  cursor: pointer;

  outline: 0;
  opacity: ${({ $isLoading }) => ($isLoading ? 0.5 : 1)};
  transition:
    opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 2ms,
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  &:hover:not(:disabled):not(.button--disabled):not(:active) {
    color: ${({ $error }) => getButtonVariantProp($error, "colorHover")};
    background-color: ${({ $error }) =>
      getButtonVariantProp($error, "backgroundHover")};
    border-color: ${({ $error }) =>
      getButtonVariantProp($error, "borderHover")};
  }

  &:active {
    background-color: ${({ $error }) =>
      getButtonVariantProp($error, "backgroundActive")};
    border-color: ${({ $error }) =>
      getButtonVariantProp($error, "borderActive")};
    box-shadow: ${({ $error }) =>
      getButtonVariantProp($error, "borderShadowActive")};
  }

  ${({ $error, disabled, $isLoading }) =>
    (disabled || $isLoading) &&
    `
    box-shadow: none;
    background-color: ${getButtonVariantProp($error, "backgroundDisabled")};
    color: ${getButtonVariantProp($error, "disabledColor")};
    cursor: not-allowed !important;
    opacity: 0.8;
  `}

  ${removePointerEvents}
  ${space}
`;

export default StyledButton;
